import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import {
  IConnectionConfigEnvironment,
  IConnectionTypes,
  IFtpConnectionTypes,
  IPartnerApplicationTypes,
  IUsageIndicators,
} from 'types/graphqlTypes'
import { Button } from 'transfix-ui'
import { useModalState } from 'utils/hooks'
import { CONNECTION_CONFIG_TYPES } from '../ConnectionConfigTypes'
import { OutboundConnectionConfigDetails } from './OutboundConnectionConfigDetails'
import { OutboundConnectionConfigsTable } from './OutboundConnectionConfigsTable'
import { useEdiOutboundConnectionConfig } from './hooks/useEdiOutboundConnectionConfig'
import { EdiOutboundConnectionConfigModal } from './modals/EdiOutboundConnectionConfigModal/EdiOutboundConnectionConfigModal'
import { CreateFiles } from './modals/StagingTesting/CreateFiles'
import { TEdiOutboundConnectionConfigModalSchema } from './schema/EdiOutboundConnectionConfigModalSchema'

const useOutboundConnectionConfigsStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    maxWidth: '100vw',
    display: 'flex',
    overflowX: 'clip',
  },
  button: {
    marginTop: theme.spacing(1),
  },
  stagingTestingButton: {
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  text: {
    marginLeft: theme.spacing(1),
  },
}))

export type CommonConfigData = {
  disabled: boolean
  environment: IConnectionConfigEnvironment
  partnerApplicationId: string
  partnerApplicationType: IPartnerApplicationTypes
  componentSeparator: string
  destinationOverride: string
  elementSeparator: string
  groupVersionStr: string
  gsReceiverId: string
  gsSenderId: string
  interchangeVersionStr: string
  isaReceiverId: string
  isaReceiverIdQualifier: string
  isaSenderId: string
  isaSenderIdQualifier: string
  testIndicator: IUsageIndicators
  repetitionSeparator: string
  segmentSeparator: string
  expect997s: boolean
}

export type CommonFtpData = {
  host: string
  username: string
  port: number
  pathPrefix: string
  secretKey: string
  connectionType: IFtpConnectionTypes
}

export const IConnectionTypesToIFtpConnectionTypes = (connectionType: IConnectionTypes) => {
  const IConnectionTypesMapper: Record<IConnectionTypes, IFtpConnectionTypes | null> = {
    AS2: null,
    FTP: IFtpConnectionTypes.FTP,
    SFTP_WITH_PASSWORD: IFtpConnectionTypes.SFTP_WITH_PASSWORD,
    SFTP_WITH_PRIVATE_KEY: IFtpConnectionTypes.SFTP_WITH_PRIVATE_KEY,
  }

  const result = IConnectionTypesMapper[connectionType]
  return result || IFtpConnectionTypes.FTP
}

export const OutboundConnectionConfigs = () => {
  const { configId: id } = useParams<{ configId: string }>()
  const [isSidebarOpen, setIsSidebarOpen] = useState(Boolean(id))
  const history = useHistory()
  const classes = useOutboundConnectionConfigsStyles()

  const { createToggleHandler, modalState } = useModalState({
    dispatchModal: false,
    dispatchEndToEndTestingModal: false,
  })
  const openAddHandler = createToggleHandler('dispatchModal', true)
  const closeAddHandler = createToggleHandler('dispatchModal', false)

  const openEndToEndTestingHandler = createToggleHandler('dispatchEndToEndTestingModal', true)
  const closeEndToEndTestingHandler = createToggleHandler('dispatchEndToEndTestingModal', false)

  const {
    createAs2OutboundConnectionConfig,
    createFtpOutboundConnectionConfig,
    updateAs2OutboundConnectionConfig,
    updateFtpOutboundConnectionConfig,
  } = useEdiOutboundConnectionConfig()

  const submitHandler = (data: TEdiOutboundConnectionConfigModalSchema) => {
    const { connectionType } = data
    const isEdit = !!data.id

    const commonConfigData: CommonConfigData = {
      disabled: data?.isDisabled,
      environment: data?.environment,
      partnerApplicationId: data?.company.id,
      partnerApplicationType: data?.company.data.__typename,
      componentSeparator: data?.componentSeparator,
      destinationOverride: data?.destinationOverride,
      elementSeparator: data?.elementSeparator,
      groupVersionStr: data?.groupVersion,
      gsReceiverId: data?.gsReceiverId,
      gsSenderId: data?.gsSenderId,
      interchangeVersionStr: data?.interchangeVersion,
      isaReceiverId: data?.isaReceiverId,
      isaReceiverIdQualifier: data?.isaReceiverIdQualifier,
      isaSenderId: data?.isaSenderId,
      isaSenderIdQualifier: data?.isaSenderIdQualifier,
      testIndicator: data?.testIndicator,
      repetitionSeparator: data?.repetitionSeparator,
      segmentSeparator: data?.segmentSeparator,
      expect997s: data?.expect997s,
    }

    if (connectionType === IConnectionTypes.AS2) {
      if (isEdit) {
        updateAs2OutboundConnectionConfig({
          ...commonConfigData,
          as2ConnectionConfigId: data?.as2ConnectionConfigId,
          id: data?.id,
        })
      } else {
        createAs2OutboundConnectionConfig({
          ...commonConfigData,
          as2ConnectionConfigId: data?.as2ConnectionConfigId,
        })
      }
    } else {
      const ftpConnectionType = IConnectionTypesToIFtpConnectionTypes(connectionType)

      const commonFtpConfigData: CommonFtpData = {
        host: data?.host,
        username: data?.username,
        port: data?.port,
        pathPrefix: data?.pathPrefix,
        secretKey: data?.secretKey,
        connectionType: ftpConnectionType,
      }

      if (isEdit) {
        updateFtpOutboundConnectionConfig({
          id: data?.id,
          ...commonFtpConfigData,
          ...commonConfigData,
        })
      } else {
        createFtpOutboundConnectionConfig({
          ...commonFtpConfigData,
          ...commonConfigData,
        })
      }
    }
  }

  /*
   * When URL param id updates, it sets new state of Sidebar panel
   */
  useEffect(() => {
    setIsSidebarOpen(Boolean(id))
  }, [id])

  const closeDialogHandler = () => {
    setIsSidebarOpen(false)
    history.replace(`/connection-configs/${CONNECTION_CONFIG_TYPES.outbound}`)
  }

  return (
    <div className={classes.root}>
      <Box pl={3}>
        <EdiOutboundConnectionConfigModal
          title='Add a new Outbound Connection Config'
          onClose={closeAddHandler}
          open={modalState.dispatchModal}
          onSubmit={submitHandler}
        />
        <CreateFiles
          title='Outbound Connection Config Staging Testing'
          onClose={closeEndToEndTestingHandler}
          open={modalState.dispatchEndToEndTestingModal}
        />
        <Grid container direction='row' justifyContent='space-between' alignItems='center'>
          <Grid item xs={12} md='auto'>
            <Typography
              variant='h3'
              className={classes.text}
              color='primary'
              display='inline'
              component='div'>
              EDI Outbound Connection Configurations
            </Typography>
          </Grid>
          <Grid item xs={12} md='auto'>
            <Button
              color='primary'
              size='small'
              data-test='add-outbound-config-button'
              className={classes.button}
              onClick={openAddHandler}>
              Add New
            </Button>
            {__ENV__ !== 'production' && (
              <Button
                className={classes.stagingTestingButton}
                color='primary'
                size='small'
                data-test='add-outbound-config-button'
                onClick={openEndToEndTestingHandler}>
                Staging Testing
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid container direction='row' alignItems='center'>
          <Grid item xs={12} md='auto'>
            <OutboundConnectionConfigsTable onEdit={submitHandler} />
          </Grid>
        </Grid>
        {isSidebarOpen && (
          <OutboundConnectionConfigDetails
            onEdit={submitHandler}
            onSidebarClose={closeDialogHandler}
          />
        )}
      </Box>
    </div>
  )
}
